import { useState } from 'react';
import api from '../utils/api';

export default function PurchaseOrderEditDrawer({ po, onClose, onSaved, isAdmin }) {
  const [form, setForm] = useState({
    po_number: po.po_number || '',
    period_start: po.period_start || '',
    period_end: po.period_end || '',
    approved_hours: po.approved_hours || '',
    timesheet_hours: po.timesheet_hours || '',
    sell_rate: po.sell_rate || '',
    buy_rate: po.buy_rate || '',
    status: po.status || 'open',
    notes: po.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Admins can override the normal open-only lock on rates/hours — needed
  // for correcting historical records. Everyone else follows the rule.
  const ratesLocked = po.status !== 'open' && !isAdmin;
  const hoursLocked = po.status !== 'open' && !isAdmin;

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const payload = {
        po_number: form.po_number,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        notes: form.notes || null,
        status: form.status,
      };
      // Hours and rates lock once a PO is invoiced/closed (unless the
      // current user is an admin) — only send them when actually editable,
      // so we never even attempt a change the backend would reject anyway.
      if (!hoursLocked) {
        payload.approved_hours = form.approved_hours ? Number(form.approved_hours) : null;
        payload.timesheet_hours = form.timesheet_hours ? Number(form.timesheet_hours) : null;
      }
      if (!ratesLocked) {
        payload.sell_rate = form.sell_rate ? Number(form.sell_rate) : null;
        payload.buy_rate = form.buy_rate ? Number(form.buy_rate) : null;
      }
      await api.put(`/purchase-orders/${po.id}`, payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save Purchase Order');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="edit-drawer">
        <div className="drawer-header">
          <h3 className="drawer-title">Edit Purchase Order — {po.po_number}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="drawer-body">
          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 18 }}>
              {error}
            </div>
          )}

          <div className="form-grid">
            <div className="form-field span-2">
              <label>PO Number</label>
              <input value={form.po_number} onChange={e => set('po_number', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Period start</label>
              <input type="date" value={form.period_start} onChange={e => set('period_start', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Period end</label>
              <input type="date" value={form.period_end} onChange={e => set('period_end', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Approved hours {hoursLocked && <span className="form-hint">(locked — not open)</span>}</label>
              <input type="number" step="0.01" value={form.approved_hours} onChange={e => set('approved_hours', e.target.value)} disabled={hoursLocked} />
            </div>
            <div className="form-field">
              <label>Timesheet hours {hoursLocked && <span className="form-hint">(locked — not open)</span>}</label>
              <input type="number" step="0.01" value={form.timesheet_hours} onChange={e => set('timesheet_hours', e.target.value)} disabled={hoursLocked} />
            </div>

            <div className="form-field">
              <label>Sell rate ($/hr) {ratesLocked && <span className="form-hint">(locked — not open)</span>}</label>
              <input type="number" step="0.01" value={form.sell_rate} onChange={e => set('sell_rate', e.target.value)} disabled={ratesLocked} />
            </div>
            <div className="form-field">
              <label>Buy rate ($/hr) {ratesLocked && <span className="form-hint">(locked — not open)</span>}</label>
              <input type="number" step="0.01" value={form.buy_rate} onChange={e => set('buy_rate', e.target.value)} disabled={ratesLocked} />
            </div>

            <div className="form-field span-2">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="open">Open</option>
                <option value="invoiced">Invoiced</option>
                <option value="closed">Closed (historical)</option>
                <option value="void">Void (canceled by Hexaware)</option>
              </select>
              <span className="form-hint">Normally this changes automatically (an AR invoice marks it Invoiced; voiding that invoice reopens it). Use this dropdown only to correct historical records.</span>
            </div>

            <div className="form-field span-2">
              <label>Notes</label>
              <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </>
  );
}
